import React, {Component} from 'react';
import {CSSTransitionGroup as ReactCSSTransitionGroup} from 'react-transition-group';
import {observer, inject} from 'mobx-react';
import {observable, action, makeObservable} from 'mobx';

//helpers
import {styleable} from 'decorators';

//lodash
import find from 'lodash/find';
import debounce from 'lodash/debounce';

//components
import Icon from 'components/Icon';

//styled-components
import {VideoPlayer, ReplayIcon, Video, FullScreenIcon, Overlay} from './styles';

@inject('store')
@observer
@styleable
class VideoPlayerComponent extends Component {
  @observable isFullscreen = false;
  @observable showOverlay = false;
  @observable videoEnded = false;
  @action setShowOverlay = value => (this.showOverlay = value);
  @action toggleOverlay = () => (this.showOverlay = !this.showOverlay);
  playPromise;

  constructor(props) {
    super(props);
    makeObservable(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {autoplayVideo: wasCurrent} = this.props;

    if (nextProps.autoplayVideo && this.video && !this.videoEnded) {
      this.replay();
    }

    if (wasCurrent && !nextProps.autoplayVideo) {
      this.reset();
    }
  }

  componentDidMount() {
    const {autoplayVideo} = this.props;

    if (this.video) {
      this.listenToVideoEvents();
      this.listenToFullscreenChange();
    }

    if (autoplayVideo && this.video && !this.videoEnded) {
      this.replay();
    }
  }

  @action
  listenToVideoEvents = () => {
    this.video.onended = () => {
      if (this.props.autoplayVideo) {
        this.onVideoEnded();
      }
    };

    this.video.onplay = () => {
      if (this.props.autoplayVideo) {
        this.videoEnded = false;
      }
    };

    this.video.onseeking = debounce(() => {
      if (this.props.autoplayVideo && this.isFullscreen) {
        const seekedToTheEnd = this.compareTimes(this.video.duration, this.video.currentTime);
        this.videoEnded = seekedToTheEnd;
      }
    }, 500);
  };

  getMinutesAndSeconds = value => {
    const minutes = parseInt(value / 60, 10);
    const seconds = parseInt(value % 60, 10);
    return {minutes, seconds};
  };

  compareTimes = (milliseconds1, milliseconds2) => {
    const time1 = this.getMinutesAndSeconds(milliseconds1);
    const time2 = this.getMinutesAndSeconds(milliseconds2);
    return time1.minutes === time2.minutes && time1.seconds === time2.seconds;
  };

  createVideoPlayPromise = ({currentTime} = {}) =>
    new Promise((resolve, reject) => {
      try {
        if (typeof currentTime !== 'undefined') {
          this.video.currentTime = 0;
        }
        this.video.play();
        resolve();
      } catch (error) {
        reject(error);
      }
    });

  @action
  fullScreenHandler = () => {
    const isFullScreen = document.fullScreen || document.mozFullScreen || document.webkitIsFullScreen;
    this.isFullscreen = isFullScreen;
    this.onFullScreenChange(isFullScreen);
  };

  onFullScreenChange = isFullScreen => {
    if (!isFullScreen) {
      if (this.videoEnded) {
        this.resetVideo();
      } else {
        const {app} = this.props.store;
        app.setDisableResizers(false);
        this.setShowOverlay(false);
        if (this.video) {
          this.playPromise = this.createVideoPlayPromise();
        }
      }
    }
  };

  listenToFullscreenChange = () => {
    document.addEventListener('fullscreenchange', this.fullScreenHandler, false);
    document.addEventListener('webkitfullscreenchange', this.fullScreenHandler, false);
    document.addEventListener('mozfullscreenchange', this.fullScreenHandler, false);
    this.video.addEventListener('webkitendfullscreen', this.fullScreenHandler, false);
  };

  @action
  onVideoEnded = () => {
    this.setShowOverlay(true);
    this.videoEnded = true;
  };

  @action
  reset = () => {
    this.setShowOverlay(false);
    this.resetVideo();
    this.videoEnded = false;

    if (this.playPromise) {
      this.playPromise.then(() => {
        this.video.pause();
      });
    }
  };

  resetVideo = () => {
    this.video.currentTime = 0;
  };

  @action
  replay = e => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    this.videoEnded = false;
    this.playPromise = this.createVideoPlayPromise({currentTime: 0})
      .then(() => {
        this.setShowOverlay(false);
      })
      .catch(() => {
        this.setShowOverlay(true);
      });
  };

  @action
  requestFullScreen = e => {
    const {app} = this.props.store;
    app.setDisableResizers(true);
    e.stopPropagation();
    e.preventDefault();

    const possibleFns = [
      'requestFullscreen',
      'mozRequestFullScreen',
      'webkitRequestFullscreen',
      'webkitEnterFullScreen'
    ];
    const fullscreenFn = find(possibleFns, fn => this.video[fn]);
    this.video[fullscreenFn]();
    this.setShowOverlay(false);
  };

  render() {
    const {src, type, poster, muted} = this.props;
    const {showOverlay, videoEnded} = this;

    return (
      <VideoPlayer>
        <Video
          onClick={this.toggleOverlay}
          ref={ref => (this.video = ref)}
          playsInline
          poster={poster}
          {...(muted && {muted})}
        >
          <source src={src} type={type} />
          Your browser does not support the video tag.
        </Video>

        <ReactCSSTransitionGroup transitionName="fadein" transitionEnterTimeout={200} transitionLeaveTimeout={300}>
          {(showOverlay || videoEnded) && (
            <Overlay onClick={this.toggleOverlay}>
              <ReplayIcon onClick={this.replay}>
                <Icon name={'refresh'} />
              </ReplayIcon>
              {!videoEnded && (
                <FullScreenIcon onClick={this.requestFullScreen}>
                  <Icon name={'resize'} />
                </FullScreenIcon>
              )}
            </Overlay>
          )}
        </ReactCSSTransitionGroup>
      </VideoPlayer>
    );
  }
}

export default VideoPlayerComponent;
